import type { ButtonProps } from '@mui/material'
import {
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material'
import type { FC, ReactNode } from 'react'
import { cloneElement, createElement as h, useRef } from 'react'
import { toast } from 'react-toastify'
import BahamutIcon from '~icons/arcticons/bahamut'
import BiFacebook from '~icons/bi/facebook'
import BiLine from '~icons/bi/line'
import BiPinterest from '~icons/bi/pinterest'
import DcinsideIcon from 'src/assets/icon/dcinside.svg?react'
import NoteIcon from 'src/assets/icon/note.svg?react'
import { OutboundLink } from 'src/components/OutboundLink'
import { useCopy } from 'src/hooks/useCopy'
import { getLocale, type SupportedLocaleCodes } from 'src/stores/intl'
import { formatParamURL } from 'src/utils/qs'
import { type AppRouteHandle, useMatches } from 'src/utils/router/route'
import { track } from 'src/utils/tracker'
// import BiReddit from '~icons/bi/reddit'
import BiTwitterX from '~icons/bi/twitter-x'
import BiWhatsapp from '~icons/bi/whatsapp'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import IcBaselineContentCopy from '~icons/ic/baseline-content-copy'
import IcBaselineDiscord from '~icons/ic/baseline-discord'
import IcBaselineIosShare from '~icons/ic/baseline-ios-share'
import LogosMessenger from '~icons/logos/messenger'
import MdiInstagram from '~icons/mdi/instagram'
import RiTiktokFill from '~icons/ri/tiktok-fill'
import SimpleIconsHatenabookmark from '~icons/simple-icons/hatenabookmark'
import SimpleIconsPixiv from '~icons/simple-icons/pixiv'
import SimpleIconsPlurk from '~icons/simple-icons/plurk'
import SimpleIconsThreads from '~icons/simple-icons/threads'

interface FormatShareURLOptions {
  link?: string
  image?: string
  text?: string
  tags?: string[]
}

export const sharePlatformMap = {
  twitter: {
    color: '#000',
    label: 'X (Formerly Twitter)',
    icon: <BiTwitterX />,
    shareUrl: ({ link, text, tags }: FormatShareURLOptions) =>
      formatParamURL('https://x.com/intent/post', {
        url: link,
        text: [
          text,
          '@PixAI_Official',
          tags?.length ? `#${tags.join(' #')}` : undefined,
        ]
          .filter(Boolean)
          .join(' '),
      }),
  },
  pinterest: {
    color: '#fff',
    label: 'Pinterest',
    icon: (
      <BiPinterest
        style={{ width: '100%', height: '100%', color: '#e70024' }}
      />
    ),
    shareUrl: ({ link, image }: FormatShareURLOptions) =>
      formatParamURL('https://www.pinterest.com/pin/create/button/', {
        url: link,
        media: image,
        method: 'button',
      }),
  },
  /* Disable reddit sharing until they stop banning us */
  // reddit: {
  //   color: '#ff4500',
  //   label: 'Reddit',
  //   icon: <BiReddit />,
  //   shareUrl: ({ link, text }: FormatShareURLOptions) =>
  //     formatParamURL('https://www.reddit.com/submit', {
  //       url: link,
  //       title: text,
  //     }),
  // },
  whatsapp: {
    color: '#25D366',
    label: 'WhatsApp',
    icon: <BiWhatsapp />,
    shareUrl: ({ link, text }: FormatShareURLOptions) =>
      formatParamURL('https://wa.me/', { text: `${text} ${link}` }),
  },
  line: {
    color: '#06c755',
    label: 'LINE',
    icon: <BiLine />,
    shareUrl: ({ link, text }: FormatShareURLOptions) =>
      formatParamURL('https://social-plugins.line.me/lineit/share', {
        url: link,
        text,
      }),
  },
  facebook: {
    color: '#fff',
    label: 'Facebook',
    icon: (
      <BiFacebook style={{ width: '100%', height: '100%', color: '#0865fe' }} />
    ),
    shareUrl: ({ link }: FormatShareURLOptions) =>
      formatParamURL('https://www.facebook.com/sharer/sharer.php', { u: link }),
  },
  hatena: {
    color: '#00a4de',
    label: 'Hatena Bookmark',
    icon: <SimpleIconsHatenabookmark />,
    shareUrl: ({ link }: FormatShareURLOptions) =>
      formatParamURL('https://b.hatena.ne.jp/entry/panel/', { url: link }),
  },
  threads: {
    color: '#000',
    label: 'Threads',
    icon: <SimpleIconsThreads />,
    shareUrl: ({ link, text }: FormatShareURLOptions) =>
      formatParamURL('https://www.threads.net/intent/post', {
        text: [text, link].filter(Boolean).join('\n'),
      }),
  },
  plurk: {
    color: '#ff574d',
    label: 'Plurk',
    icon: <SimpleIconsPlurk />,
    shareUrl: ({ link, text }: FormatShareURLOptions) =>
      formatParamURL('https://www.plurk.com/', {
        qualifier: 'shares',
        status: [text, link].filter(Boolean).join(' '),
      }),
  },
  pixiv: {
    color: '#0099fa',
    label: 'Pixiv',
    icon: <SimpleIconsPixiv />,
    shouldCopy: true,
    shareUrl: ({ tags }: FormatShareURLOptions) =>
      formatParamURL('https://www.pixiv.net/illustration/create', {
        tag: tags?.[0],
      }),
  },
  messenger: {
    color: '#0084ff',
    label: 'Messenger',
    icon: <LogosMessenger />,
    shouldCopy: true,
    shareUrl: () => 'https://www.messenger.com/',
  },
  instagram: {
    color: '#e4405f',
    label: 'Instagram',
    icon: <MdiInstagram />,
    shouldCopy: true,
    shareUrl: () => 'https://www.instagram.com/',
  },
  tiktok: {
    color: '#000',
    label: 'TikTok',
    icon: <RiTiktokFill />,
    shouldCopy: true,
    shareUrl: () => 'https://www.tiktok.com/',
  },
  discord: {
    color: '#5865f2',
    label: 'Discord',
    icon: <IcBaselineDiscord />,
    shouldCopy: true,
    shareUrl: () => 'https://discord.com/',
  },
  dcinside: {
    color: '#3B468F',
    label: 'Dcinside',
    icon: <DcinsideIcon />,
    shouldCopy: true,
    shareUrl: () => 'https://www.dcinside.com/',
  },
  note: {
    color: '#08131a',
    textColor: '#fff',
    borderColor: 'rgba(0,0,0,0.2)',
    label: 'Note',
    icon: <NoteIcon />,
    shouldCopy: true,
    shareUrl: () => 'https://note.com/',
  },
  bahamut: {
    color: '#00a0e9',
    label: 'Bahamut',
    icon: (
      <BahamutIcon
        style={{
          strokeWidth: 1.5,
          transform: 'scale(1.4)',
        }}
      />
    ),
    shouldCopy: true,
    shareUrl: () => 'https://forum.gamer.com.tw/',
  },
} as const

type PlatformType = keyof typeof sharePlatformMap

/*
 * Channel ordering per locale, based on:
 * - Global click data: twitter 542k, line 326k, pinterest 249k, whatsapp 202k,
 *   facebook 135k, hatena 60k, pixiv 60k, discord 18k, instagram 14k
 * - Regional adjustments: LINE/Pixiv/Hatena prioritized for ja,
 *   Threads/Bahamut/Plurk for zh (Taiwan), Dcinside for ko
 */
const platformsByLocale = {
  en: [
    'twitter',
    'pinterest',
    'whatsapp',
    'facebook',
    'discord',
    'instagram',
    'messenger',
    'pixiv',
    'tiktok',
    'line',
  ],
  ja: [
    'twitter',
    'line',
    'pixiv',
    'hatena',
    'pinterest',
    'whatsapp',
    'facebook',
    'discord',
    'instagram',
    'note',
  ],
  ko: [
    'twitter',
    'instagram',
    'facebook',
    'discord',
    'dcinside',
    'pinterest',
    'whatsapp',
    'line',
    'pixiv',
    'messenger',
  ],
  zh: [
    'twitter',
    'threads',
    'facebook',
    'instagram',
    'discord',
    'pinterest',
    'whatsapp',
    'bahamut',
    'plurk',
    'pixiv',
  ],
} satisfies Partial<Record<SupportedLocaleCodes, readonly PlatformType[]>>

export const getPlatformsByLocale = (
  locale: SupportedLocaleCodes,
): PlatformType[] => {
  const platforms =
    (platformsByLocale as Record<string, readonly PlatformType[]>)[locale] ??
    platformsByLocale.en
  return [...platforms]
}

const PRIMARY_PLATFORM_COUNT = 4

export const getPrimaryPlatforms = (
  locale: SupportedLocaleCodes,
): PlatformType[] =>
  getPlatformsByLocale(locale).slice(0, PRIMARY_PLATFORM_COUNT)

export const getSecondaryPlatforms = (
  locale: SupportedLocaleCodes,
): PlatformType[] => getPlatformsByLocale(locale).slice(PRIMARY_PLATFORM_COUNT)

interface ShareButtonProps {
  type: keyof typeof sharePlatformMap
  link: string
  image?: string
  text?: string
  tags?: string[]
  buttonProps?: ButtonProps
  render?: (props: {
    onClick: React.MouseEventHandler<HTMLButtonElement>
    icon: ReactNode
    label: string
    color: string
  }) => ReactNode
  afterAction?: () => void
}

type SharePlatform = (typeof sharePlatformMap)[keyof typeof sharePlatformMap]
type CopyPlatform = Extract<SharePlatform, { shouldCopy: true }>

const isCopyPlatform = (platform: SharePlatform): platform is CopyPlatform =>
  'shouldCopy' in platform && platform.shouldCopy === true

export const ShareButton: FC<ShareButtonProps> = ({
  type,
  link,
  image,
  text,
  tags,
  buttonProps,
  render,
  afterAction,
}) => {
  const [copyToClipboard] = useCopy()
  const platform = sharePlatformMap[type]
  const { color, label, icon } = platform
  const textColor =
    'textColor' in platform && typeof platform.textColor === 'string'
      ? platform.textColor
      : '#fff'
  const borderColor =
    'borderColor' in platform && typeof platform.borderColor === 'string'
      ? platform.borderColor
      : null
  const route = useMatches().at(-1) as { handle: AppRouteHandle }
  const copyPlatform = isCopyPlatform(platform) ? platform : null
  const socialUrl = platform.shareUrl({
    link: formatParamURL(link, {
      utm_medium: 'referral',
      utm_source: type,
      utm_content: 'share',
    }),
    image,
    text,
    tags,
  })

  const onClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    track('share_social_btn_click', {
      type,
      scene: route.handle.name,
    })

    if (copyPlatform) {
      e.preventDefault()
      e.stopPropagation()
      window.open(socialUrl, '_blank', 'noopener,noreferrer')
      copyToClipboard(
        formatParamURL(link, {
          utm_medium: 'referral',
          utm_source: type,
          utm_content: 'share',
        }),
        { scene: route.handle.name },
      )
      afterAction?.()
    }
  }

  const clonedIcon = cloneElement(icon, {
    className: 'size-8',
  })

  const inner = render ? (
    render({ onClick, icon: clonedIcon, label, color })
  ) : (
    <Button className="w-full py-2" onClick={onClick} {...buttonProps}>
      <div className="flex flex-col gap-1 items-center normal-case">
        <div
          className="size-12 inline-grid place-items-center rounded-full"
          style={{
            backgroundColor: color,
            color: textColor,
            ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
          }}
        >
          {clonedIcon}
        </div>
        <div>{label}</div>
      </div>
    </Button>
  )

  if (copyPlatform) {
    return (
      <OutboundLink href={socialUrl} className="no-underline">
        {inner}
      </OutboundLink>
    )
  }

  return (
    <OutboundLink
      href={socialUrl}
      className="no-underline"
      onClick={afterAction}
    >
      {inner}
    </OutboundLink>
  )
}

export const getArtworkShareLink = (id: string) =>
  `${location.origin}/artwork/${id}`
export const getModelShareLink = (id: string) =>
  `${location.origin}/model/${id}`

const SocialShare: FC<{
  link?: string
  image?: string
  text?: string
  tags?: string[]
  afterAction?: () => void
}> = ({ image, text, tags, afterAction, link }) => {
  if (!link) return null
  const platforms = getPlatformsByLocale(getLocale())

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-wrap">
        {platforms.map(type =>
          h(ShareButton, {
            key: type,
            type,
            link,
            image,
            text,
            tags,
            afterAction,
          }),
        )}
      </div>

      <ItemShareLink link={link} afterAction={afterAction} />
    </div>
  )
}

export const SystemShareButton: FC<{
  link: string
  text: string
  image?: string
  afterAction?: () => void
}> = ({ link, text, image, afterAction }) => {
  const route = useMatches().at(-1) as { handle: AppRouteHandle }

  const onClick: React.MouseEventHandler<HTMLButtonElement> = async e => {
    e.preventDefault()
    e.stopPropagation()

    const getImage = async () => {
      const blob = await fetch(image!).then(res => res.blob())
      return blob
    }

    if (navigator.share) {
      navigator
        .share({
          title: 'PixAI Art',
          text,
          url: link,
          ...(image
            ? { files: [new File([await getImage()], 'image.png')] }
            : {}),
        })
        .then(() => {
          track('share_system_btn_click', {
            type: 'system',
            scene: route.handle.name,
          })
        })
        .catch(err => {
          console.error(err)
        })
    } else {
      toast.warn('System share is not supported')
    }
    afterAction?.()
  }

  return (
    <IconButton
      className="border border-gray-300 border-solid"
      onClick={onClick}
    >
      <IcBaselineIosShare className="size-6" />
    </IconButton>
  )
}

export const ItemShareLink: FC<{
  className?: string
  link: string
  afterAction?: () => void
}> = ({ className, link, afterAction }) => {
  const [copyToClipboard, copied] = useCopy()
  const route = useMatches().at(-1) as { handle: AppRouteHandle }
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <OutlinedInput
      className={className}
      value={link}
      readOnly
      size="small"
      inputProps={{
        style: {
          userSelect: 'all',
        },
      }}
      style={{
        padding: 0,
      }}
      inputRef={inputRef}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            onClick={() => {
              track('share_copy_click', { scene: route.handle.name })
              copyToClipboard(formatParamURL(link, { utm_source: 'copy_web' }))
              afterAction?.()
            }}
          >
            {copied ? (
              <FluentCheckmark16Filled className="size-6 text-green-500" />
            ) : (
              <IcBaselineContentCopy className="size-6" />
            )}
          </IconButton>
        </InputAdornment>
      }
      onMouseEnter={() => {
        inputRef.current?.select()
      }}
      onMouseLeave={() => {
        window.getSelection()?.removeAllRanges()
        inputRef.current?.blur()
      }}
    />
  )
}

export default SocialShare

export const ClickToCopy: FC<{
  content: string
  className?: string
  children?: ReactNode
}> = ({ content, className, children }) => {
  const [copyToClipboard, copied] = useCopy()
  const defaultIcon = copied ? (
    <FluentCheckmark16Filled className="size-6 text-green-500" />
  ) : (
    <IcBaselineContentCopy className="size-6" />
  )
  return (
    <ButtonBase
      onClick={() => {
        copyToClipboard(content)
      }}
      className={className}
    >
      {children ?? defaultIcon}
    </ButtonBase>
  )
}
